import { REGEX_SPECIAL_CHARS } from 'common/components/FormulaEditor/utils/regexUtils';

/**
 * Removes all whitespace characters from a formula string
 */
export const removeSpaces = (formula: string): string => formula.replace(/\s+/g, '');

/**
 * Filters out entries with empty or whitespace-only formulas
 */
export const filterEmptyFormulas = (formulas: { [columnName: string]: string }): Array<[string, string]> => {
  return Object.entries(formulas).filter(([, formula]) => formula.trim() !== '');
};

/**
 * Processes formulas by removing spaces and filtering empty values
 * Returns a clean object with non-empty formulas
 */
export const processFormulas = (formulas: {
  [columnName: string]: string;
}): { [columnName: string]: string } => {
  return Object.fromEntries(
    Object.entries(formulas)
      .map(([key, formula]) => [key, removeSpaces(formula)])
      .filter(([, formula]) => formula !== ''),
  );
};

/**
 * Escapes special regex characters in a string
 */
const escapeRegExp = (str: string) => str.replace(REGEX_SPECIAL_CHARS, '\\$&');

/**
 * Encloses matched terms in backticks
 */
export const wrapFormulaTerms = (formula: string, terms: string[]): string => {
  if (!terms.length) return formula;

  const sortedTerms = [...terms].sort((a, b) => b.length - a.length);
  const pattern = sortedTerms.map(escapeRegExp).join('|');
  const regex = new RegExp(`(${pattern})`, 'g');

  return formula.replace(regex, '`$1`');
};

export const BACKTICK_WRAPPED_TEXT = /`([^`]+)`/g;

export const unwrapFormulaTerms = (formula: string): string => {
  return formula.replace(BACKTICK_WRAPPED_TEXT, '$1');
};

export const extractFormulaTerms = (formula: string): string[] => {
  const matches = formula.match(BACKTICK_WRAPPED_TEXT);
  if (!matches) return [];
  return matches.map((m) => m.replace(/`/g, ''));
};
